import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Users,
  Award,
  Code,
  Megaphone,
  Link2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Coins,
  Bot,
  Globe,
  Filter,
  Wallet,
  Lock,
  Flame,
  Twitter,
  TrendingUp,
} from "lucide-react";

const GROWTH_STATS_ENDPOINT = "/project/api/growth-stats";
const DAILY_GROWTH_STATS_ENDPOINT = "/project/api/daily-growth-stats";

// Changelog data organized by date
const changelog = [
  // March 2026
  {
    date: "Mar 29, 2026",
    category: "platform",
    title: "💰 Hidden Trading Rewards — Auto-Detection & Claiming",
    desc: "RefundYourSOL now detects and claims hidden PumpFun and PumpSwap trading rewards (cashback + creator fees). Claim transactions are batched with account closures in a single signature. Available on the website and via API (/api/closeTokenAccounts returns a rewardClaim field).",
    link: "https://refundyoursol.com/blog/refundyoursol-rewards-claiming-update",
    highlight: true,
  },
  {
    date: "Mar 29, 2026",
    category: "platform",
    title: "🔤 .sol Domain Names in Leaderboards & Wallet Button",
    desc: "Wallet addresses now resolve to .sol domain names (via Bonfida SNS) across the leaderboard, wallet button, and referral views. .skr (Solana Mobile Seeker) support is ready and activates when domains are minted on-chain.",
  },
  {
    date: "Mar 29, 2026",
    category: "platform",
    title: "📱 Seeker Mobile Detection & Push Notifications",
    desc: "Automatic Solana Mobile Seeker device detection in Phantom's in-app browser. Push notification opt-in for mobile users with email fallback. Notification prompt shown after wallet cleanup when no accounts remain.",
  },
  {
    date: "Mar 29, 2026",
    category: "platform",
    title: "📊 Rejection Survey & Email Subscriptions",
    desc: "New rejection survey for first-time users who cancel transactions — helps identify UX friction. Email subscription option in the notification modal. All data viewable in the admin Push Notifications dashboard.",
  },
  {
    date: "Mar 17, 2026",
    category: "platform",
    title: "🤖 RefundYourSOL MCP — AI Agent Toolkit",
    desc: "Released @refundyoursol/mcp — a Model Context Protocol server with 7 tools for AI agents. Wallet cleanup, token trading on 12+ DEXes, and market data. Works with Claude Desktop, Cursor, Windsurf, and more. Published on npm and registered on 8004market.io.",
    link: "https://www.npmjs.com/package/@refundyoursol/mcp",
    highlight: true,
  },
  {
    date: "Mar 16, 2026",
    category: "platform",
    title: "📝 Blog Launch — 15 SEO-Optimized Articles",
    desc: "Launched the RefundYourSOL blog with 15 articles covering guides, tool comparisons, and developer documentation. Includes competitor comparisons, trading API guide, and MCP announcement.",
    link: "https://refundyoursol.com/blog",
    highlight: true,
  },
  {
    date: "Mar 15, 2026",
    category: "platform",
    title: "⚡ Performance & Stability Fixes",
    desc: "Fixed event loop blocking from getProgramAccounts calls (was causing 502 errors). Disabled slow RPC fallbacks in PumpSwap and Raydium DEX integrations. Hardcoded Jito UUID for reliable MEV protection. Reduced server RPC proxy timeout for heavy calls.",
  },
  {
    date: "Mar 15, 2026",
    category: "staking",
    title: "🎯 Staking Claim Fix — Higher CU Limit",
    desc: "Fixed staking reward claims failing for positions with many unclaimed days. Increased compute unit limit from 200K to 300K to handle 100+ days of accrued rewards in a single transaction.",
  },
  {
    date: "Mar 15, 2026",
    category: "platform",
    title: "🔍 SEO & LLM Visibility Overhaul",
    desc: "Added JSON-LD structured data (FAQPage, SoftwareApplication, HowTo, WebSite schemas). Fixed robots.txt blocking /docs. Updated sitemap with all blog URLs. Submitted to IndexNow and Google Search Console for faster indexing.",
  },
  {
    date: "Mar 15, 2026",
    category: "platform",
    title: "🏅 New Rank Names",
    desc: "Renamed all 10 XP ranks to match the RefundYourSOL brand: Dust Sweeper, Rent Seeker, Token Pruner, SOL Reclaimer, Account Purger, Rent Reaper, Refund Pro, Chain Sweeper, SOL Savant, and Refund Legend.",
  },
  {
    date: "Mar 5, 2026",
    category: "bot",
    title: "📊 Phantom Metrics Tracker",
    desc: "Built an automated tracker that monitors Phantom marketplace rankings for RefundYourSOL and competitors every 5 minutes. Sends Telegram notifications when RefundYourSOL enters or exits the Top 50, or when rank improves.",
  },
  // February 2026
  {
    date: "Feb 19, 2026",
    category: "community",
    title: "🏆 RYS Contest Is Live",
    desc: "RYS Contest is officially out! Complete tasks in the Telegram bot — X/Twitter, Discord, Telegram, raiding, reviews, platform usage, and more — to earn points and climb the leaderboard. All contest tasks are live and validated. Track your progress and rewards on the project website.",
    link: "https://refundyoursol.com/project",
    highlight: true,
  },
  {
    date: "Feb 9, 2026",
    category: "staking",
    title: "⏳ Staking Upgrades: 1-Week Lock + Early Unlock (-35%)",
    desc: "Staking has been upgraded with a 1-week lock option (shown as “1 Week” instead of “0 month”), plus early unlock with a 35% $RYS token penalty sent to the burn wallet. Added UI support for early withdraw, and expanded staking-topic notifications (new stake + early withdraw penalty events).",
    link: "https://refundyoursol.com/stake",
    highlight: true,
  },
  // January 2026
  {
    date: "Jan 30, 2026",
    category: "bot",
    title: "💱 USDC to SOL Swap in Wallets Menu",
    desc: "Added USDC to SOL swap feature directly in the Telegram bot's wallets menu! Perfect for wallets that don't support swaps natively (like PumpFun wallets). Access it via /wallets → Manage Wallet → Extra Features → Swap USDC → SOL. Get real-time quotes and execute swaps seamlessly.",
    link: "https://t.me/refundyoursolbot",
  },
  {
    date: "Jan 30, 2026",
    category: "community",
    title: "🏆 RYS Contest Thorough Testing",
    desc: "RYS Contest was thoroughly tested — every task in the bot verified and validated. Contest launched Feb 19, 2026.",
    link: "https://refundyoursol.com/project",
  },
  {
    date: "Jan 30, 2026",
    category: "community",
    title: "🎨 RYS Discord Server Redesign",
    desc: "Complete Discord server redesign with 10+ new channels added! Enhanced community experience with new commands, improved referral system, Discord account linking, and preparations for upcoming RYS contests. Join the revamped community and explore all the new features!",
    link: "https://discord.gg/8RPqhgyjXt",
    highlight: true,
  },
  {
    date: "Jan 3, 2026",
    category: "community",
    title: "😎 Custom RYS Emojis Added to Discord",
    desc: "Fresh custom RYS emojis are now available in our Discord server! Express yourself with exclusive emotes featuring the RYS brand. Join the community and start using them today!",
    link: "https://discord.gg/8RPqhgyjXt",
  },
  // December 2025
  {
    date: "Dec 20, 2025",
    category: "platform",
    title: "🎯 Promo Banner System with A/B/C Testing",
    desc: `Launched smart promotional banners across the platform to highlight RYS Bot, Auto-Clean, and the Referral Program. Features multiple banner styles, color variations, and built-in analytics to track conversion rates (CVR). A/B/C testing runs automatically to determine the highest-performing creative for each placement.`,
    link: "https://refundyoursol.com",
    highlight: true,
  },
  {
    date: "Dec 18, 2025",
    category: "platform",
    title: "⚡ Smart Batch Optimizer API",
    desc: `New useOptimizer=true parameter for the smartest wallet cleaning on Solana! Our intelligent system batches transactions optimally to achieve the lowest possible blockchain fees. Perfect for market makers, holder tools, and anyone managing multiple wallets — clean at the cheapest price the Solana network allows.`,
    link: "https://refundyoursol.com/docs#batch-optimizer",
    highlight: true,
  },
  {
    date: "Dec 16, 2025",
    category: "platform",
    title: "📚 Documentation Portal Launch",
    desc: "Full API documentation is now live! Explore guides, examples, and interactive endpoints for integrating RYS into your projects.",
    link: "https://refundyoursol.com/docs",
    highlight: true,
  },
  {
    date: "Dec 14, 2025",
    category: "listing",
    title: "📱 OKX Wallet Listed",
    desc: "RefundYourSOL is now listed as a dApp in OKX Wallet — connect and clean your wallet from the OKX Web3 experience.",
    link: "https://www.okx.com/web3",
  },
  {
    date: "Dec 10, 2025",
    category: "listing",
    title: "📱 Seeker Mobile App Store Listed!",
    desc: "RefundYourSOL is now officially listed on the Seeker Mobile App Store — reaching 150K+ Solana Saga and Seeker device users! To celebrate, all Seeker wallet users get 0% platform fees for a limited time.",
    link: "https://refundyoursol.com",
    highlight: true,
  },
  {
    date: "Dec 5, 2025",
    category: "platform",
    title: "WSOL/USDC Refund Option on API",
    desc: "Added WSOL and USDC as refund options on the API — choose your preferred token when recovering SOL from closed accounts.",
    link: "https://refundyoursol.com/docs",
  },
  {
    date: "Dec 4, 2025",
    category: "milestone",
    title: "Project Website Launch & DexScreener #1",
    desc: "Official project website goes LIVE! Also trending #1 on DexScreener with boosted visibility. Major milestone for RYS exposure.",
    link: "https://dexscreener.com/solana/8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump",
    highlight: true,
  },
  {
    date: "Dec 2, 2025",
    category: "platform",
    title: "📱 New Sleek App Design ✨",
    desc: `Complete website redesign — both before connecting your wallet & after!

🎨 Landing Page:
✅ Brand new hero section
✅ "Why Choose RefundYourSOL" feature showcase
✅ Partners & Listings (CMC, CoinGecko, Jupiter, Phantom)

📲 Mobile:
✅ Native app-like bottom navigation (for PWA users)
✅ Optimized layouts for all screen sizes

💡 After Closing Accounts:
✅ New "Quick Tips" modal — fee reductions, auto-clean & more

🛠 Other:
✅ Discord tickets added to Support
✅ Updated Terms of Service

Modern, cleaner, safer, and easier to use — especially on mobile! 💜`,
    link: "https://refundyoursol.com",
    highlight: true,
  },
  // November 2025
  {
    date: "Nov 28, 2025",
    category: "listing",
    title: "Solscan Wallet Labeling",
    desc: "All RYS wallets now officially labeled on Solscan — Staking Program, Staking Vault, Staking Treasury, Fee Wallet & Fee Payer. Full transparency and professional presentation for the 65% staked supply.",
    link: "https://solscan.io/account/6QwqgT13oVGhFkMhJwRV6f6nQq52Duy2HqovtiMHaNgs",
    highlight: true,
  },
  {
    date: "Nov 22, 2025",
    category: "vcs",
    title: "Community Call EP18",
    desc: "11,000+ active users this week, 62.4% supply staked, raiding system spotlight",
  },
  {
    date: "Nov 15, 2025",
    category: "vcs",
    title: "Community Call EP17",
    desc: "14,000+ active users, staking at 62.4%, spotlight tech series launched",
  },
  {
    date: "Nov 8, 2025",
    category: "milestone",
    title: "500K Total Users",
    desc: "Platform reaches 500,000 total users milestone — 400K+ gained since token launch",
    highlight: true,
  },
  {
    date: "Nov 3, 2025",
    category: "platform",
    title: "API Improvements & Safety Burn",
    desc: "Multi-wallet API, closing/burning API, safety burn API all improved at /docs",
    link: "https://refundyoursol.com/docs",
  },
  {
    date: "Oct 31, 2025",
    category: "platform",
    title: "Mobile UI Optimization",
    desc: "Staking UI fixed for mobile devices — overlapping buttons and menus resolved",
  },
  {
    date: "Oct 30, 2025",
    category: "platform",
    title: "Token Data Integration",
    desc: "Upgraded token data pipeline — 10s+ loading reduced to <2s with better accuracy",
    highlight: true,
  },
  {
    date: "Oct 17, 2025",
    category: "bot",
    title: "Staking & Bot Fixes",
    desc: "Staking rewards delay fixed, whales verification fixed, auto-cleanup now uses unlimited API endpoint",
  },
  // October 2025
  {
    date: "Oct 11, 2025",
    category: "vcs",
    title: "Community Call EP12",
    desc: "22,000+ active users, leading in Nigeria, Indonesia, Bangladesh",
  },
  {
    date: "Oct 4, 2025",
    category: "milestone",
    title: "Monthly Highlights - September",
    desc: "140K+ new users in September, highest refund platform, Phantom verified",
  },
  // September 2025
  {
    date: "Sep 27, 2025",
    category: "vcs",
    title: "Community Call EP10",
    desc: "19,000+ new users, buybot ready for testing, market-making platform in development",
  },
  {
    date: "Sep 20, 2025",
    category: "vcs",
    title: "Community Call EP9",
    desc: "47,000+ new users, new ATH of $3.1M MC, 63% supply staked",
  },
  {
    date: "Sep 15, 2025",
    category: "milestone",
    title: "Whale Exit Handled",
    desc: "Early whale exited position — supply now healthier and more decentralized",
  },
  {
    date: "Sep 13, 2025",
    category: "vcs",
    title: "Community Call EP8",
    desc: "28K+ new users, 2.5M token accounts closed, new ATH past $2M MC",
  },
  {
    date: "Sep 13, 2025",
    category: "bot",
    title: "Raid Bot Live",
    desc: "Raid bot feature completed and in testing with communities",
  },
  {
    date: "Sep 6, 2025",
    category: "listing",
    title: "YouTube Channel Launch",
    desc: "Official RYS YouTube channel live with tutorials and updates",
    link: "https://youtube.com/@refundyoursol",
  },
  {
    date: "Sep 3, 2025",
    category: "staking",
    title: "Auto-Compound Launch",
    desc: "Auto-compound feature live — boosts APY by ~30% automatically",
    highlight: true,
  },
  {
    date: "Sep 2, 2025",
    category: "listing",
    title: "Phantom Wallet Listed",
    desc: "Officially listed as dApp on Phantom — 115K+ transactions, 24K+ users tracked",
    link: "https://phantom.com/apps/refundyoursol",
    highlight: true,
  },
  {
    date: "Sep 1, 2025",
    category: "marketing",
    title: "MadApes AMA",
    desc: "$300 community rewards, partnership with MadApes community",
  },
  // August 2025
  {
    date: "Aug 30, 2025",
    category: "vcs",
    title: "Community Call EP6",
    desc: "100K+ new users in past weeks, 55% supply staked, 2M+ accounts closed",
  },
  {
    date: "Aug 23, 2025",
    category: "platform",
    title: "2022 SPL Tokens Support",
    desc: "Old 2022 SPL tokens now supported — close up to 23 tokens per transaction",
    highlight: true,
  },
  {
    date: "Aug 22, 2025",
    category: "staking",
    title: "Staking APY Boost",
    desc: "Rewards now based on total staked supply instead of total token supply — massive APY increase",
  },
  {
    date: "Aug 20, 2025",
    category: "marketing",
    title: "PAAL AI Collaboration",
    desc: "Live spaces with PAAL AI (800M ATH) — major influencer partnership",
  },
  {
    date: "Aug 17, 2025",
    category: "platform",
    title: "Staking UI Updates",
    desc: "Refresh button, referral % bonus display, staking stats in statistics",
  },
  {
    date: "Aug 16, 2025",
    category: "staking",
    title: "Staking Platform Launch",
    desc: "RYS Staking goes live — 1/3/6 month locks, daily claimable rewards, 50% revenue sharing",
    highlight: true,
  },
  {
    date: "Aug 15, 2025",
    category: "vcs",
    title: "Community Call EP5",
    desc: "10,000 holders reached, Crypto Vic partnership announced",
  },
  {
    date: "Aug 12, 2025",
    category: "platform",
    title: "Server Upgrade",
    desc: "Servers upgraded to 3x stronger version for better speed and reliability",
  },
  {
    date: "Aug 8, 2025",
    category: "platform",
    title: "Fee Structure Update",
    desc: "Tiered fee reduction: 10-50% off based on RYS holdings (10K-500K tokens)",
  },
  {
    date: "Aug 4, 2025",
    category: "marketing",
    title: "Major Trending Campaign",
    desc: "$6,600+ spent on OKX, Bitget, Solscan, GeckoTerminal, Birdeye, CMC, CoinGecko trendings",
  },
  {
    date: "Aug 1, 2025",
    category: "milestone",
    title: "Weekly Community Calls Launch",
    desc: "First weekly community call — $1M-$1.5M daily volume, website nearing 200K users, 1.3M accounts closed",
    highlight: true,
  },
  // July 2025
  {
    date: "Jul 31, 2025",
    category: "bot",
    title: "Whales Group Filter",
    desc: "Whales group filtered to 2.5M+ holders only, verification improved",
  },
  {
    date: "Jul 30, 2025",
    category: "bot",
    title: "Wallet Import Launch",
    desc: "Import wallets via private key, seed phrase, or key array — auto/manual cleanup",
    highlight: true,
  },
  {
    date: "Jul 28, 2025",
    category: "bot",
    title: "Buybot Improvements",
    desc: "More buy emojis, 86 premium emoji limit, improved JUP APIs",
  },
  {
    date: "Jul 25, 2025",
    category: "platform",
    title: "Referral Rate Increase",
    desc: "Referral rewards increased from 30% to 35% — highest in industry",
  },
  {
    date: "Jul 23, 2025",
    category: "marketing",
    title: "Major Buybot Stream",
    desc: "Live on Major Buybot Stream at 4pm UTC — bot making waves",
  },
  {
    date: "Jul 22, 2025",
    category: "bot",
    title: "Buy Bot Launch",
    desc: "RYS Buy Bot live — supports all Solana AMMs, animated stickers, custom emojis",
    highlight: true,
  },
  {
    date: "Jul 21, 2025",
    category: "platform",
    title: "Referral Rate Increase",
    desc: "Referral rewards increased to 35% — highest among all competitors",
  },
  {
    date: "Jul 18, 2025",
    category: "milestone",
    title: "50 SOL Burned",
    desc: "50 SOL worth of $RYS bought and burned forever — demonstrating swap system impact",
    highlight: true,
  },
  {
    date: "Jul 17, 2025",
    category: "listing",
    title: "DEXScreener Profile Updated",
    desc: "Full DEXScreener profile with new branding",
  },
  {
    date: "Jul 16, 2025",
    category: "listing",
    title: "X Community Created",
    desc: "Official X community launched for RYS holders",
    link: "https://x.com/i/communities/1938025807126867975",
  },
  {
    date: "Jul 15, 2025",
    category: "listing",
    title: "Farcaster dApp & Profile",
    desc: "RefundYourSOL listed as a dApp on Farcaster with official project profile created.",
    link: "https://warpcast.com/refundyoursol",
  },
  {
    date: "Jul 15, 2025",
    category: "bot",
    title: "Group Management Features",
    desc: "Mention all users, custom commands, bot blocking, report & vote system",
  },
  {
    date: "Jul 14, 2025",
    category: "milestone",
    title: "Bubblemap Clusters Fixed",
    desc: "InsightX 24% cluster and Bubblemaps 40% cluster false flags resolved",
  },
  {
    date: "Jul 8, 2025",
    category: "milestone",
    title: "First VC Session",
    desc: "Community VC with announcements: Bitget, Phantom listings, Solana Mobile app planned",
  },
  {
    date: "Jul 6, 2025",
    category: "listing",
    title: "Jupiter Verified",
    desc: "Token verified on Jupiter aggregator — top 10 on verify.jup.ag",
    link: "https://jup.ag/tokens/8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump",
    highlight: true,
  },
  {
    date: "Jul 6, 2025",
    category: "platform",
    title: "Wallet Support Expanded",
    desc: "Coinbase, Backpack, Solflare batch transactions + swap support added",
  },
  {
    date: "Jul 4, 2025",
    category: "milestone",
    title: "New ATH",
    desc: "Token reaches new all-time high with strong community support",
  },
  {
    date: "Jul 3, 2025",
    category: "listing",
    title: "CoinMarketCap Listed",
    desc: "Listed on CoinMarketCap — major visibility milestone",
    link: "https://coinmarketcap.com/currencies/refundyoursol/",
    highlight: true,
  },
  {
    date: "Jul 3, 2025",
    category: "platform",
    title: "Swap Integration",
    desc: "Swap for $RYS integrated — 30% lower fees when swapping rewards to RYS",
    highlight: true,
  },
  {
    date: "Jul 1, 2025",
    category: "listing",
    title: "CoinGecko Verified",
    desc: "Token verified on CoinGecko before CMC listing",
    link: "https://www.coingecko.com/en/coins/refundyoursol",
  },
  {
    date: "Jul 1, 2025",
    category: "listing",
    title: "Solscan Verified",
    desc: "Token profile verified on Solscan explorer",
    link: "https://solscan.io/account/8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump",
  },
  // June 2025
  {
    date: "Jun 30, 2025",
    category: "bot",
    title: "Distribution Options",
    desc: "Choose to donate daily rewards to marketing via @refundyoursolbot",
  },
  {
    date: "Jun 29, 2025",
    category: "bot",
    title: "Holder Portal Launch",
    desc: "Holder verification, whale groups (0.25% supply), holder-only polls",
  },
  {
    date: "Jun 28, 2025",
    category: "milestone",
    title: "Whale Chat Launch",
    desc: "Whales chat for 0.5%+ holders, airdrop verification, partnership submissions",
  },
  {
    date: "Jun 26, 2025",
    category: "milestone",
    title: "First Rewards Distribution",
    desc: "First holder rewards distributed — 50% of revenue to holders with 100K+ tokens",
  },
  {
    date: "Jun 25, 2025",
    category: "milestone",
    title: "$RYS Token Launch",
    desc: "RefundYourSOL token launched — 50% lower fees for holders, 50% revenue sharing",
    highlight: true,
  },
  // May 2025
  {
    date: "May 29, 2025",
    category: "listing",
    title: "SolHouse Signal Partnership",
    desc: "Partnership with SolHouse Signal — 12K+ subscribers Solana alpha channel",
    link: "https://t.me/solhousesignal",
    highlight: true,
  },
  // March 2025
  {
    date: "Mar 7, 2025",
    category: "milestone",
    title: "1,100 Users & 62 SOL Refunded",
    desc: "Crazy two weeks: 30,760 token accounts closed, 62.72 SOL ($9,094) refunded to users",
  },
  {
    date: "Mar 1, 2025",
    category: "platform",
    title: "Safe Burning Feature",
    desc: "Introducing 'Burn Safely' option — mistakenly burned tokens can now be recovered",
    highlight: true,
  },
  // February 2025
  {
    date: "Feb 23, 2025",
    category: "milestone",
    title: "Community Testimonial",
    desc: "TheCryptern shares receiving 1.6 SOL from closing token accounts — organic growth begins",
  },
  {
    date: "Feb 20, 2025",
    category: "milestone",
    title: "401 Users & Growing",
    desc: "14.10 SOL refunded, 6,439 token accounts closed — steady growth continues",
  },
  {
    date: "Feb 16, 2025",
    category: "platform",
    title: "Token Burning Feature",
    desc: "Burn scammy, unknown, or malicious tokens & close their empty accounts for FREE SOL",
  },
  {
    date: "Feb 12, 2025",
    category: "listing",
    title: "Security Audit Partner",
    desc: "Partnership for trust, safety & security auditing",
    highlight: true,
  },
  {
    date: "Feb 5, 2025",
    category: "milestone",
    title: "First 207 Users",
    desc: "Two weeks in: 1,504 accounts closed, 4.03 SOL (~$850) refunded — honest work begins!",
  },
  {
    date: "Feb 3, 2025",
    category: "listing",
    title: "Solana Vibe Station Partnership",
    desc: "Partnership with Solana Vibe Station community",
    link: "https://discord.com/channels/1241508519190986752/1337246848154599547",
  },
  // January 2025
  {
    date: "Jan 18, 2025",
    category: "milestone",
    title: "Platform Launch",
    desc: "RefundYourSOL is now live! Get back the SOL that the blockchain keeps from you.",
    link: "https://refundyoursol.com",
    highlight: true,
  },
];

const categoryConfig = {
  all: { label: "All Updates", icon: Filter, color: "text-purple-400", bg: "bg-purple-500/20" },
  milestone: { label: "Milestones", icon: Award, color: "text-yellow-400", bg: "bg-yellow-500/20" },
  platform: { label: "Platform", icon: Globe, color: "text-blue-400", bg: "bg-blue-500/20" },
  staking: { label: "Staking", icon: Coins, color: "text-pink-400", bg: "bg-pink-500/20" },
  bot: { label: "RYS Bot", icon: Bot, color: "text-orange-400", bg: "bg-orange-500/20" },
  listing: { label: "Listings", icon: Link2, color: "text-green-400", bg: "bg-green-500/20" },
  marketing: { label: "Marketing", icon: Megaphone, color: "text-cyan-400", bg: "bg-cyan-500/20" },
  community: { label: "Community", icon: Users, color: "text-indigo-400", bg: "bg-indigo-500/20" },
  vcs: { label: "VCs", icon: Users, color: "text-violet-400", bg: "bg-violet-500/20" },
};

// Clean graph component with tooltips
const MilestoneGraph = ({ data, label, color, valueKey, formatValue, isLoading }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  const values = data.map((d) => d[valueKey]);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const padding = (maxValue - minValue) * 0.15 || maxValue * 0.1;
  const graphMax = maxValue + padding;
  const graphMin = Math.max(0, minValue - padding);
  const range = graphMax - graphMin || 1;
  
  const gradientId = `grad-${label.replace(/\s+/g, '')}`;
  
  // SVG dimensions
  const width = 400;
  const height = 120;
  const marginLeft = 0;
  const marginRight = 0;
  const marginTop = 20;
  const marginBottom = 10;
  const chartWidth = width - marginLeft - marginRight;
  const chartHeight = height - marginTop - marginBottom;
  
  // Calculate positions
  const getX = (i) => marginLeft + (i / Math.max(data.length - 1, 1)) * chartWidth;
  const getY = (val) => marginTop + (1 - (val - graphMin) / range) * chartHeight;
  
  // Build path
  const points = data.map((p, i) => ({ x: getX(i), y: getY(p[valueKey]), value: p[valueKey], label: p.label }));
  
  let linePath = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    linePath += ` L ${points[i].x} ${points[i].y}`;
  }
  
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - marginBottom} L ${points[0].x} ${height - marginBottom} Z`;
  
  // Grid lines (4 horizontal lines)
  const gridLines = [0, 0.33, 0.66, 1].map(pct => marginTop + pct * chartHeight);
  
  return (
    <div className="glass rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-400 flex items-center gap-2">
          {label}
          {isLoading && <span className="text-xs text-purple-400">● Live</span>}
        </h4>
        <span className="text-xl font-bold" style={{ color }}>
          {formatValue(values[values.length - 1] || 0)}
        </span>
      </div>
      
      {/* Graph */}
      <div className="relative">
        {/* Y-axis labels */}
        <div className="absolute -left-1 top-0 bottom-5 w-10 flex flex-col justify-between text-[9px] text-gray-500 text-right">
          <span>{formatValue(graphMax)}</span>
          <span>{formatValue((graphMax + graphMin) / 2)}</span>
          <span>{formatValue(graphMin)}</span>
        </div>
        
        {/* Chart */}
        <div className="ml-10 relative">
          <svg 
            viewBox={`0 0 ${width} ${height}`}
            className="w-full overflow-visible"
            style={{ height: '120px' }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.4" />
                <stop offset="100%" stopColor={color} stopOpacity="0.05" />
              </linearGradient>
            </defs>
            
            {/* Grid lines */}
            {gridLines.map((y, i) => (
              <line 
                key={i}
                x1={marginLeft} 
                y1={y} 
                x2={width - marginRight} 
                y2={y} 
                stroke="rgba(255,255,255,0.08)" 
                strokeWidth="1" 
              />
            ))}
            
            {/* Area */}
            <path d={areaPath} fill={`url(#${gradientId})`} />
            
            {/* Line */}
            <path 
              d={linePath} 
              fill="none" 
              stroke={color} 
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Points with hover areas */}
            {points.map((p, i) => (
              <g key={i}>
                {/* Visible point */}
                <circle 
                  cx={p.x} 
                  cy={p.y} 
                  r={hoveredIndex === i ? 6 : 4}
                  fill="#111119"
                  stroke={color}
                  strokeWidth="2"
                  style={{ transition: 'r 0.15s ease' }}
                  pointerEvents="none"
                />
                {/* Larger invisible hover area (must be topmost; otherwise hovering the dot flickers) */}
                <circle 
                  cx={p.x} 
                  cy={p.y} 
                  r="15"
                  fill="#000"
                  fillOpacity="0"
                  pointerEvents="all"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseMove={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
              </g>
            ))}
          </svg>
          
          {/* Tooltip */}
          {hoveredIndex !== null && points[hoveredIndex] && (
            <div 
              className="absolute pointer-events-none bg-gray-900/95 border border-white/20 rounded-lg px-3 py-2 text-xs whitespace-nowrap z-20 shadow-xl"
              style={{
                left: `calc(${(points[hoveredIndex].x / width) * 100}% + ${points[hoveredIndex].x < width / 2 ? '10px' : '-10px'})`,
                top: `calc(${(points[hoveredIndex].y / height) * 100}% - 8px)`,
                transform: points[hoveredIndex].x < width / 2 ? 'translateY(-100%)' : 'translate(-100%, -100%)',
              }}
            >
              <div className="text-gray-400 mb-0.5">{points[hoveredIndex].label}</div>
              <div className="font-bold text-sm" style={{ color }}>{formatPreciseNumber(points[hoveredIndex].value)}</div>
            </div>
          )}
          
          {/* X-axis labels */}
          <div className="flex justify-between text-[9px] text-gray-500 mt-1">
            {data.map((p, i) => (
              <span key={i}>{p.label?.replace(' 2025', '')}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Precise number formatting for tooltips (shows full detail)
const formatPreciseNumber = (val) => {
  if (val >= 1_000_000_000) return `${(val / 1_000_000_000).toFixed(3).replace(/\.?0+$/, '')}B`;
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(3).replace(/\.?0+$/, '')}M`;
  if (val >= 1_000) return val.toLocaleString();
  return String(Math.round(val));
};

// Configuration for daily growth metrics tabs
const dailyMetricsConfig = {
  totalStaked: {
    label: "Total Staked",
    valueKey: "totalStaked",
    color: "#a855f7", // purple
    icon: Lock,
    formatValue: (val) => {
      if (val >= 1_000_000_000) return `${(val / 1_000_000_000).toFixed(2)}B`;
      if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
      if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
      return String(Math.round(val));
    },
    formatTooltip: formatPreciseNumber,
    description: "RYS tokens staked",
  },
  burned: {
    label: "Burned",
    valueKey: "burned",
    color: "#f97316", // orange
    icon: Flame,
    formatValue: (val) => {
      if (val >= 1_000_000_000) return `${(val / 1_000_000_000).toFixed(2)}B`;
      if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
      if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
      return String(Math.round(val));
    },
    formatTooltip: formatPreciseNumber,
    description: "RYS tokens burned",
  },
  botUsers: {
    label: "Bot Users",
    valueKey: "botUsers",
    color: "#22c55e", // green
    icon: Bot,
    formatValue: (val) => {
      if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
      if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
      return String(Math.round(val));
    },
    formatTooltip: formatPreciseNumber,
    description: "Telegram bot users",
  },
  walletsConnected: {
    label: "Wallets",
    valueKey: "walletsConnected",
    color: "#3b82f6", // blue
    icon: Wallet,
    formatValue: (val) => {
      if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
      if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
      return String(Math.round(val));
    },
    formatTooltip: formatPreciseNumber,
    description: "Wallets connected to bot",
  },
  xAccountsConnected: {
    label: "X Accounts",
    valueKey: "xAccountsConnected",
    color: "#8b5cf6", // violet
    icon: Twitter,
    formatValue: (val) => {
      if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
      if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
      return String(Math.round(val));
    },
    formatTooltip: formatPreciseNumber,
    description: "X/Twitter accounts connected",
  },
};

// Tabbed graph component for daily metrics
const DailyMetricsGraph = ({ data, selectedMetric, onSelectMetric, isLoading }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  const config = dailyMetricsConfig[selectedMetric];
  const values = data.map((d) => d[config.valueKey] || 0);
  const maxValue = Math.max(...values, 1);
  const minValue = Math.min(...values);
  const padding = (maxValue - minValue) * 0.15 || maxValue * 0.1;
  const graphMax = maxValue + padding;
  const graphMin = Math.max(0, minValue - padding);
  const range = graphMax - graphMin || 1;
  
  const gradientId = `grad-daily-${selectedMetric}`;
  
  // SVG dimensions - use full width
  const width = 1000; // Increased from 600 to ensure full width usage
  const height = 150;
  const marginLeft = 0;
  const marginRight = 0;
  const marginTop = 25;
  const marginBottom = 15;
  const chartWidth = width - marginLeft - marginRight;
  const chartHeight = height - marginTop - marginBottom;
  
  // Calculate positions
  const getX = (i) => marginLeft + (i / Math.max(data.length - 1, 1)) * chartWidth;
  const getY = (val) => marginTop + (1 - ((val || 0) - graphMin) / range) * chartHeight;
  
  const isJan18 = (d) => {
    if (!d) return false;
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return false;
    return dt.getMonth() === 0 && dt.getDate() === 18;
  };

  // Build path
  const points = data.map((p, i) => ({ 
    x: getX(i), 
    y: getY(p[config.valueKey] || 0), 
    value: p[config.valueKey] || 0, 
    label: p.label,
    date: p.date,
  }));

  // Annotation: Jan 18 drop was due to X API downtime (only show on X Accounts tab)
  const annotationIndex =
    selectedMetric === 'xAccountsConnected'
      ? data.findIndex((d) => isJan18(d?.date))
      : -1;
  const annotationPoint = annotationIndex >= 0 ? points[annotationIndex] : null;
  
  let linePath = points.length > 0 ? `M ${points[0].x} ${points[0].y}` : '';
  for (let i = 1; i < points.length; i++) {
    linePath += ` L ${points[i].x} ${points[i].y}`;
  }
  
  const areaPath = points.length > 0 
    ? `${linePath} L ${points[points.length - 1].x} ${height - marginBottom} L ${points[0].x} ${height - marginBottom} Z`
    : '';
  
  // Grid lines (4 horizontal lines)
  const gridLines = [0, 0.33, 0.66, 1].map(pct => marginTop + pct * chartHeight);
  
  // Show every Nth label based on data length
  const labelInterval = data.length > 14 ? 7 : data.length > 7 ? 3 : 1;
  
  const Icon = config.icon;
  
  return (
    <div className="glass rounded-xl p-4">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(dailyMetricsConfig).map(([key, cfg]) => {
          const TabIcon = cfg.icon;
          return (
            <button
              key={key}
              onClick={() => onSelectMetric(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                selectedMetric === key
                  ? "bg-white/10 ring-1"
                  : "text-gray-500 hover:text-gray-300 hover:bg-white/5"
              }`}
              style={{
                color: selectedMetric === key ? cfg.color : undefined,
                ringColor: selectedMetric === key ? `${cfg.color}40` : undefined,
              }}
            >
              <TabIcon className="w-3.5 h-3.5" />
              {cfg.label}
            </button>
          );
        })}
      </div>
      
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-400 flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: config.color }} />
          {config.label} Growth
          {isLoading && <span className="text-xs text-purple-400">● Tracking</span>}
        </h4>
        <div className="text-right">
          <span className="text-xl font-bold" style={{ color: config.color }}>
            {config.formatValue(values[values.length - 1] || 0)}
          </span>
          <p className="text-[10px] text-gray-500">{config.description}</p>
        </div>
      </div>
      
      {/* No data message */}
      {data.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Tracking starts today!</p>
          <p className="text-xs mt-1">Check back tomorrow to see the growth graph.</p>
        </div>
      )}
      
      {/* Graph */}
      {data.length > 0 && (
        <div className="relative">
          {/* Y-axis labels */}
          <div className="absolute -left-1 top-0 bottom-5 w-12 flex flex-col justify-between text-[9px] text-gray-500 text-right pr-2">
            <span>{config.formatValue(graphMax)}</span>
            <span>{config.formatValue((graphMax + graphMin) / 2)}</span>
            <span>{config.formatValue(graphMin)}</span>
          </div>
          
          {/* Chart */}
          <div className="ml-12 relative" style={{ width: 'calc(100% - 3rem)', marginRight: 0, paddingRight: 0 }}>
            <svg 
              viewBox={`0 0 ${width} ${height}`}
              className="w-full overflow-visible"
              style={{ height: '150px', minWidth: '100%' }}
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={config.color} stopOpacity="0.4" />
                  <stop offset="100%" stopColor={config.color} stopOpacity="0.05" />
                </linearGradient>
              </defs>
              
              {/* Grid lines */}
              {gridLines.map((y, i) => (
                <line 
                  key={i}
                  x1={marginLeft} 
                  y1={y} 
                  x2={width - marginRight} 
                  y2={y} 
                  stroke="rgba(255,255,255,0.08)" 
                  strokeWidth="1" 
                />
              ))}
              
              {/* Area */}
              {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}
              
              {/* Line */}
              {linePath && (
                <path 
                  d={linePath} 
                  fill="none" 
                  stroke={config.color} 
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
              
              {/* Points with hover areas */}
              {points.map((p, i) => (
                <g key={i}>
                  {/* Visible point */}
                  <circle 
                    cx={p.x} 
                    cy={p.y} 
                    r={hoveredIndex === i ? 6 : 4}
                    fill="#111119"
                    stroke={config.color}
                    strokeWidth="2"
                    style={{ transition: 'r 0.15s ease' }}
                    pointerEvents="none"
                  />
                  {/* Larger invisible hover area (must be topmost; otherwise hovering the dot flickers) */}
                  <circle 
                    cx={p.x} 
                    cy={p.y} 
                    r="15"
                    fill="#000"
                    fillOpacity="0"
                    pointerEvents="all"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseMove={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  />
                </g>
              ))}
            </svg>
            
            {/* Jan 18 annotation (X Accounts tab only) */}
            {annotationPoint && (
              <div
                className="absolute pointer-events-none bg-gray-900/90 border border-white/15 text-gray-200 rounded-md px-2 py-1 text-[10px] whitespace-nowrap z-10 shadow-lg"
                style={{
                  left: `calc(${(annotationPoint.x / width) * 100}% + ${annotationPoint.x < width / 2 ? '10px' : '-10px'})`,
                  top: `calc(${(annotationPoint.y / height) * 100}% - 22px)`,
                  transform: annotationPoint.x < width / 2 ? 'translateY(-100%)' : 'translate(-100%, -100%)',
                }}
              >
                X API downtime
              </div>
            )}

            {/* Tooltip */}
            {hoveredIndex !== null && points[hoveredIndex] && (
              <div 
                className="absolute pointer-events-none bg-gray-900/95 border border-white/20 rounded-lg px-3 py-2 text-xs whitespace-nowrap z-20 shadow-xl"
                style={{
                  left: `calc(${(points[hoveredIndex].x / width) * 100}% + ${points[hoveredIndex].x < width / 2 ? '10px' : '-10px'})`,
                  top: `calc(${(points[hoveredIndex].y / height) * 100}% - 8px)`,
                  transform: points[hoveredIndex].x < width / 2 ? 'translateY(-100%)' : 'translate(-100%, -100%)',
                }}
              >
                <div className="text-gray-400 mb-0.5">{points[hoveredIndex].label}</div>
                <div className="font-bold text-sm" style={{ color: config.color }}>
                  {(config.formatTooltip || config.formatValue)(points[hoveredIndex].value)}
                </div>
              </div>
            )}
            
            {/* X-axis labels */}
            <div className="relative text-[9px] text-gray-500 mt-1" style={{ height: '14px', width: '100%', marginRight: 0, paddingRight: 0 }}>
              {data.map((p, i) => {
                if (i % labelInterval !== 0 && i !== data.length - 1) return null;
                // Calculate position to match graph points exactly
                const xPercent = (i / Math.max(data.length - 1, 1)) * 100;
                const isFirst = i === 0;
                const isLast = i === data.length - 1;
                return (
                  <span 
                    key={i} 
                    className="absolute whitespace-nowrap"
                    style={{ 
                      left: isFirst ? '0' : isLast ? '100%' : `${xPercent}%`,
                      transform: isFirst ? 'none' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                    }}
                  >
                    {p.label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Changelog() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showAllItems, setShowAllItems] = useState(false);
  const [growthData, setGrowthData] = useState({ monthly: [], current: { totalUsers: 0, totalAccounts: 0 } });
  const [statsLoaded, setStatsLoaded] = useState(false);
  
  // Daily growth stats state
  const [dailyGrowthData, setDailyGrowthData] = useState({ daily: [], totalDays: 0 });
  const [dailyStatsLoaded, setDailyStatsLoaded] = useState(false);
  const [selectedDailyMetric, setSelectedDailyMetric] = useState("totalStaked");

  // Fetch growth stats from API (once - historical data doesn't change)
  useEffect(() => {
    let isMounted = true;
    
    const fetchGrowthStats = async () => {
      try {
        const response = await fetch(GROWTH_STATS_ENDPOINT);
        if (response.ok && isMounted) {
          const data = await response.json();
          if (data?.success && data?.data) {
            setGrowthData(data.data);
            setStatsLoaded(true);
          }
        }
      } catch (err) {
        console.error("Failed to fetch growth stats for changelog:", err);
      }
    };
    
    fetchGrowthStats();
  }, []);
  
  // Fetch daily growth stats from API
  useEffect(() => {
    let isMounted = true;
    
    const fetchDailyGrowthStats = async () => {
      try {
        const response = await fetch(DAILY_GROWTH_STATS_ENDPOINT);
        if (response.ok && isMounted) {
          const data = await response.json();
          if (data?.success && data?.data) {
            setDailyGrowthData(data.data);
            setDailyStatsLoaded(true);
          }
        }
      } catch (err) {
        console.error("Failed to fetch daily growth stats for changelog:", err);
      }
    };
    
    fetchDailyGrowthStats();
  }, []);


  // Transform monthly data into graph format
  const userMilestones = statsLoaded && growthData.monthly.length > 0
    ? growthData.monthly.map((m) => ({
        date: m.label,
        users: m.users,
        label: m.label, // Use date as label for X-axis
        formattedValue: m.users >= 1000000 
          ? `${(m.users / 1000000).toFixed(1)}M` 
          : m.users >= 1000 
          ? `${(m.users / 1000).toFixed(0)}K` 
          : String(m.users),
      }))
    : [
        { date: "Jun 25", users: 115000, label: "Jun 2025", formattedValue: "115K" },
        { date: "Jul 25", users: 180000, label: "Jul 2025", formattedValue: "180K" },
        { date: "Aug 25", users: 280000, label: "Aug 2025", formattedValue: "280K" },
        { date: "Sep 25", users: 380000, label: "Sep 2025", formattedValue: "380K" },
        { date: "Oct 25", users: 450000, label: "Oct 2025", formattedValue: "450K" },
        { date: "Nov 25", users: 525000, label: "Nov 2025", formattedValue: "525K" },
      ];

  // Transform monthly data for accounts
  const accountsMilestones = statsLoaded && growthData.monthly.length > 0
    ? growthData.monthly.map((m) => ({
        date: m.label,
        accounts: m.accounts,
        label: m.label, // Use date as label for X-axis
        formattedValue: m.accounts >= 1000000 
          ? `${(m.accounts / 1000000).toFixed(1)}M` 
          : m.accounts >= 1000 
          ? `${(m.accounts / 1000).toFixed(0)}K` 
          : String(m.accounts),
      }))
    : [
        { date: "Jun 25", accounts: 800000, label: "Jun 2025", formattedValue: "800K" },
        { date: "Jul 25", accounts: 1200000, label: "Jul 2025", formattedValue: "1.2M" },
        { date: "Aug 25", accounts: 1800000, label: "Aug 2025", formattedValue: "1.8M" },
        { date: "Sep 25", accounts: 2500000, label: "Sep 2025", formattedValue: "2.5M" },
        { date: "Oct 25", accounts: 3200000, label: "Oct 2025", formattedValue: "3.2M" },
        { date: "Nov 25", accounts: 3800000, label: "Nov 2025", formattedValue: "3.8M" },
      ];

  const filteredChangelog =
    activeCategory === "all"
      ? changelog
      : changelog.filter((item) => item.category === activeCategory);

  const displayedChangelog = showAllItems
    ? filteredChangelog
    : filteredChangelog.slice(0, 15);

  const toggleExpand = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const formatUsers = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  const formatAccounts = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  // Calculate days since platform launch (Jan 18, 2025)
  const launchDate = new Date("2025-01-18");
  const today = new Date();
  const daysSinceLaunch = Math.floor((today - launchDate) / (1000 * 60 * 60 * 24));

  return (
    <section
      id="changelog"
      className="relative py-24 border-t border-white/5 bg-[#0a0a0f]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(126,34,206,0.1),transparent_70%)]" />
      <div className="relative max-w-5xl mx-auto px-6">
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl md:text-4xl font-bold mb-4 text-center gradient-text"
        >
          Changelog & Achievements
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-gray-400 text-center mb-12 max-w-2xl mx-auto"
        >
          {daysSinceLaunch}+ days of continuous development. From platform launch to {statsLoaded ? formatUsers(growthData.current.totalUsers) : "500K"}+ users — here's our journey.
        </motion.p>

        {/* Growth Graphs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid md:grid-cols-2 gap-4 mb-6"
        >
          <MilestoneGraph
            data={userMilestones}
            label="User Growth"
            color="#a855f7"
            valueKey="users"
            formatValue={formatUsers}
            isLoading={statsLoaded}
          />
          <MilestoneGraph
            data={accountsMilestones}
            label="Token Accounts Closed"
            color="#22c55e"
            valueKey="accounts"
            formatValue={formatAccounts}
            isLoading={statsLoaded}
          />
        </motion.div>
        
        {/* Daily Metrics Growth Graph */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-12"
        >
          <DailyMetricsGraph
            data={dailyGrowthData.daily}
            selectedMetric={selectedDailyMetric}
            onSelectMetric={setSelectedDailyMetric}
            isLoading={dailyStatsLoaded}
          />
          {dailyGrowthData.totalDays > 0 && (
            <p className="text-center text-xs text-gray-500 mt-2">
              Tracking {dailyGrowthData.totalDays} day{dailyGrowthData.totalDays !== 1 ? 's' : ''} of growth data
            </p>
          )}
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12"
        >
          {[
            { label: "Days Building", value: `${daysSinceLaunch}+`, icon: Calendar, color: "text-purple-400" },
            { label: "Total Updates", value: changelog.length + "+", icon: Code, color: "text-blue-400" },
            { label: "Major Milestones", value: changelog.filter((c) => c.highlight).length, icon: Award, color: "text-yellow-400" },
            { label: "Partnerships", value: changelog.filter((c) => c.category === "listing").length, icon: Link2, color: "text-green-400" },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="glass rounded-xl p-4 text-center">
                <Icon className={`w-6 h-6 ${stat.color} mx-auto mb-2`} />
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            );
          })}
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-wrap justify-center gap-2 mb-8"
        >
          {Object.entries(categoryConfig).map(([key, config]) => {
            const Icon = config.icon;
            const count =
              key === "all"
                ? changelog.length
                : changelog.filter((c) => c.category === key).length;

            return (
              <button
                key={key}
                onClick={() => {
                  setActiveCategory(key);
                  setShowAllItems(false);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  activeCategory === key
                    ? `${config.bg} ${config.color} ring-1 ring-current/30`
                    : "text-gray-500 hover:text-gray-300"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {config.label}
                <span className="opacity-60">({count})</span>
              </button>
            );
          })}
        </motion.div>

        {/* Timeline */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-purple-500/50 via-purple-500/20 to-transparent" />

            <div className="space-y-4">
              {displayedChangelog.map((item, index) => {
                const config = categoryConfig[item.category];
                const Icon = config.icon;
                const isExpanded = expandedItems.has(index);
                const isLeft = index % 2 === 0;

                return (
                  <motion.div
                    key={`${item.date}-${item.title}`}
                    initial={{ opacity: 0, x: isLeft ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={`relative flex items-start gap-4 ${
                      index % 2 === 0 ? "md:flex-row md:justify-end" : "md:flex-row-reverse md:justify-end"
                    }`}
                  >
                    {/* Timeline dot */}
                    <div
                      className={`absolute left-4 md:left-1/2 w-3 h-3 rounded-full md:-translate-x-1/2 ${
                        item.highlight ? "bg-yellow-500 ring-4 ring-yellow-500/20" : config.bg.replace("/20", "")
                      }`}
                    />

                    {/* Content card */}
                    <div
                      className={`ml-10 w-full md:ml-0 md:w-[calc(50%-2rem)] ${isLeft ? "md:pr-8 md:ml-0" : "md:pl-8 md:mr-0"}`}
                    >
                      <div
                        className={`glass rounded-xl p-4 border transition-all cursor-pointer hover:border-purple-500/30 ${
                          item.highlight
                            ? "border-yellow-500/30 bg-yellow-500/5"
                            : "border-white/5"
                        }`}
                        onClick={() => toggleExpand(index)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-6 h-6 rounded-full ${config.bg} flex items-center justify-center`}>
                              <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                            </div>
                            <span className="text-[10px] text-gray-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {item.date}
                            </span>
                            {item.highlight && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
                                Major
                              </span>
                            )}
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          )}
                        </div>

                        <h4 className="text-sm font-medium text-white mb-1">
                          {item.title}
                        </h4>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <p className="text-xs text-gray-400 mt-2 whitespace-pre-line">
                                {item.desc}
                              </p>
                              {item.link && (
                                <a
                                  href={item.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 mt-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Show More Button */}
            {filteredChangelog.length > 15 && !showAllItems && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center mt-8"
              >
                <button
                  onClick={() => setShowAllItems(true)}
                  className="px-6 py-2 glass rounded-full text-sm text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Show All ({filteredChangelog.length - 15} more)
                </button>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Footer Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-500 text-sm">
            Building since{" "}
            <span className="text-purple-400 font-medium">January 2025</span>
            {" "}• Shipping updates{" "}
            <span className="text-green-400 font-medium">every week</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
